import { LoadingSpinner } from '@shared/components';
import { InvoicingStatusWarning } from './InvoicingStatusWarning';
import { RaiseInvoicePanel } from './RaiseInvoicePanel';
import { CreateInvoiceButton } from './CreateInvoiceButton';
import { CreateInvoiceHeader } from './CreateInvoiceHeader';
import { InvoiceSuccessNotice } from './InvoiceSuccessNotice';
import { PreviouslyInvoicedScreen } from './PreviouslyInvoicedScreen';
import { defaultInvoicingDealerId } from '../utils/invoiceSubmitGates';
import { useInvoiceDetailsForm } from '../hooks/useInvoiceDetailsForm';
import { useRejectionFocus } from '../hooks/useRejectionFocus';
import { useResetTopUpWhenFits } from '../hooks/useFundingChoice';
import { useTopUpForm } from '../hooks/useTopUpForm';
import { minimumTopUpMinor } from '../utils/topUpAmount';
import { createInvoiceDisabledReason, settledSoldLines } from '../utils/invoicingView';
import type { Agreement } from '../types/agreements.types';
import type { AgreementInvoicing } from '../hooks/useAgreementInvoicing';

interface Props {
    agreement: Agreement;
    invoicing: AgreementInvoicing;
}

function InvoicingTabBody({ agreement, invoicing }: Props) {
    const {
        canBrowseInvoices,
        invoicesQuery,
        dealerNames,
        view,
        soldItemsQuery,
        candidatesQuery,
        submission,
        excessGross,
        overBalance,
        dealerLock,
    } = invoicing;
    const rejectionFocus = useRejectionFocus();
    const heldDealerId = dealerLock.lockedDealerId ?? dealerLock.pendingDealerId;
    const topUpPending = dealerLock.pendingDealerId !== null;
    const detailsForm = useInvoiceDetailsForm(
        heldDealerId ?? defaultInvoicingDealerId(candidatesQuery.data),
        invoicing.recordedMileage,
        submission.submit,
        (outcome) => {
            if (outcome.ok) dealerLock.release();
            view.onSubmitSettled(outcome);
            rejectionFocus.onSettled(outcome);
        },
        heldDealerId,
    );
    const topUpForm = useTopUpForm({
        agreementId: agreement.id,
        minimumMinor: minimumTopUpMinor(excessGross),
        takingDealerId: detailsForm.invoicingDealerId || null,
        onRecorded: invoicing.onTopUpRecorded,
        dealerSession: dealerLock,
        topUpPending,
        onAccessRefused: invoicing.onTopUpRefused,
        heldRecordedAmount: invoicing.recordedTopUpAmount,
    });

    useResetTopUpWhenFits(overBalance, topUpForm.reset);
    useResetTopUpWhenFits(overBalance, invoicing.motorplan.form.reset);
    const hasNoInvoices = invoicesQuery.data?.length === 0;
    const backToPreviouslyInvoiced = () => {
        topUpForm.reset();
        view.showPreviouslyInvoiced();
    };

    const notice = invoicing.submission.lastSubmittedNumber ? (
        <InvoiceSuccessNotice
            invoiceNumber={invoicing.submission.lastSubmittedNumber}
            onDismiss={invoicing.submission.dismissNotice}
        />
    ) : null;

    return (
        <div className="space-y-4">
            {agreement.status !== 'active' && <InvoicingStatusWarning status={agreement.status} />}

            {view.view === 'loading' && <LoadingSpinner message="Loading previously invoiced" className="p-4" />}

            {view.view === 'previously_invoiced' && (
                <PreviouslyInvoicedScreen
                    invoicesQuery={invoicesQuery}
                    soldItemsQuery={soldItemsQuery}
                    availableBalance={invoicing.availableBalance}
                    balanceUnreadableReason={invoicing.balanceUnreadableReason}
                    dealerNames={dealerNames}
                    headingRef={view.previouslyInvoicedHeadingRef}
                    notice={notice}
                    reversal={invoicing.reversal}
                    action={
                        <CreateInvoiceButton
                            disabledReason={createInvoiceDisabledReason(settledSoldLines(soldItemsQuery))}
                            onClick={view.showCreate}
                        />
                    }
                />
            )}

            {view.view === 'create' && (
                <>
                    <CreateInvoiceHeader
                        headingRef={view.createHeadingRef}
                        onBack={canBrowseInvoices && !hasNoInvoices ? backToPreviouslyInvoiced : undefined}
                    />
                    <RaiseInvoicePanel
                        agreement={agreement}
                        invoicing={invoicing}
                        detailsForm={detailsForm}
                        topUpForm={topUpForm}
                        dealerLocked={dealerLock.lockedDealerId !== null}
                        topUpPending={topUpPending}
                        panelRef={rejectionFocus.panelRef}
                    />
                </>
            )}
        </div>
    );
}

export function AgreementInvoicingTab({ agreement, invoicing }: Props) {
    return <InvoicingTabBody key={agreement.id} agreement={agreement} invoicing={invoicing} />;
}
